import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { Store } from '../../store/store';
import { Message, StoreNode } from '../../store';
import { WindowState } from '../overlays/windowState';
import { InputGroupState, InputState, input, inputGroup } from '../input';
import { TeamUpdateInput, VocabularyInput } from '@clipr/lib';
import { notifyError, notifySuccess } from '../../services/notifications';


export class TeamDictionaryWindowState
  extends StoreNode {

  constructor(store: Store) {
    super(store);
    makeObservable(this, {
      isLoading: observable,
      teamId: observable,
      teamDictionaryList: observable,
      hasError: computed,
      isPairIncomplete: computed,
      isSubmitDisabled: computed,
      team: computed,
      open: action,
      close: action,
      addDictionaryItem: action,
      removeDictionaryitem: action,
      init: action,
      submit: action,
      reset: action,
    });

    this.window.listen(
      this.windowListener);
  }

  readonly submitButton = input(this, {
    name: 'saveButton',
    disabled: (input: InputState) => {
      return (
        !!input.loading ||
        input.isSubmitting ||
        this.isSubmitDisabled);
    }
  });

  readonly nodeType = 'TeamDictionaryWindow';
  readonly window = new WindowState(this.store);

  isLoading = false;
  teamId: string | null = null;
  teamDictionaryList = observable.array<TeamDictionaryItemState>();

  get hasError() {
    return !!this.teamDictionaryList.find(i => i.dictionaryInputGroup.hasErrorInputs);
  }

  get isPairIncomplete() {
    return !!this.teamDictionaryList.find(i => (!i.correctWordInput.isEmpty && i.misspelledWordsInput.isEmpty));
  }

  get isSubmitDisabled() {
    return this.hasError || this.isPairIncomplete;
  }

  get team() {
    return this.store.teamManager.getTeam(this.teamId);
  }

  private windowListener = (msg: Message<WindowState>) => {
    switch (msg.type) {
      case 'close':
      case 'outsideClick':
        if (this.isLoading)
          return;
        this.close();
        break;
    }
  }

  open(teamId: string | null) {
    this.reset();
    this.teamId = teamId;

    this.init();
    this.dispatch('Overlays', 'openWindow', { name: 'TeamDictionaryWindow' });
  }

  close() {
    this.emit('TeamDictionaryWindow:close');
    this.dispatch('Overlays', 'closeWindow');
  }

  addDictionaryItem = () => {
    const teamDictionaryItemState: TeamDictionaryItemState  = new TeamDictionaryItemState(this.store);
    this.teamDictionaryList.push(teamDictionaryItemState);
  }

  removeDictionaryitem = (i: number) => {
    this.teamDictionaryList.splice(i, 1);
  }

  init() {
    if (!this.team?.vocabulary?.length) {
      this.addDictionaryItem();
      return;
    }

    this.team.vocabulary.forEach(value => {
      const teamDictionaryItemState: TeamDictionaryItemState  = new TeamDictionaryItemState(this.store);
      teamDictionaryItemState.misspelledWordsInput.loadValue(value.phrase);
      teamDictionaryItemState.correctWordInput.loadValue(value.displayAs);
      this.teamDictionaryList.push(teamDictionaryItemState);
    });
  }

  submit = async () => {
    this.isLoading = true;
    const vocabularyInput: VocabularyInput[] = this.teamDictionaryList
    .filter(teamDictionaryItem => !teamDictionaryItem.misspelledWordsInput.isEmpty)
    .map(teamDictionaryItem => ({
      phrase: teamDictionaryItem.misspelledWordsInput.value!,
      displayAs: teamDictionaryItem.correctWordInput.value || ' '
    }));

    const vars: { args: TeamUpdateInput } = {
      args: {
        teamId: this.teamId!,
        vocabulary: vocabularyInput?.length ? vocabularyInput : []
      }
    };

    const [, err] = await this.store.apiService.updateTeam(vars);

    if (err) {
      this.handleSubmitReject(`Could not update the library '${this.team?.name}'. Duplicating common misspellings is not allowed. Please try again.`);
      return;
    }

    runInAction(() => {
      this.handleSubmitSuccess(`Library '${this.team?.name}' updated successfully`);
    });
  }

  private handleSubmitReject(msg: string) {
    this.isLoading = false;
    this.window.unlock();

    notifyError(this, msg);
  }

  private handleSubmitSuccess(msg: string) {
    this.isLoading = false;
    this.window.unlock();

    this.close();
    this.emit('teamDictionaryWindow:dictionaryUpdated');

    notifySuccess(this, msg);
  }

  reset() {
    this.teamDictionaryList.clear();
  }
}

export class TeamDictionaryItemState
  extends StoreNode {

  readonly dictionaryInputGroup: InputGroupState;
  error: string | null = null;

  constructor(store: Store) {
    super(store);
    makeObservable(this, {
      error: observable,
      correctWordInput: observable,
      misspelledWordsInput: observable,
      setError: action,
    });

    this.correctWordInput = input(this, {
      name: 'correctWord',
      placeholder: 'Enter word or name, e.g. “CLIPr”',
    });

    this.misspelledWordsInput = input(this, {
      name: 'misspelledWords',
      isRequired: () => {
        return !this.correctWordInput.isEmpty
      },
      placeholder: 'e.g., “clipper”',
      error: (input) => {
        if (input.value) {
          return /^[a-zA-Z.-]+$/.test(input.value) ? 
          null : 'Only alphabetic characters and “-“ or “.“ are allowed'
        }

        if (input.isEmpty && !this.correctWordInput.isEmpty) 
          return 'Required field'
      }
    });

    this.dictionaryInputGroup = inputGroup(this, {
      name: 'dictionaryInputGroup',
      inputs: [
        this.correctWordInput,
        this.misspelledWordsInput
      ],
      disabled: (input: InputGroupState) => {
        return (
          input.hasLoadingInputs ||
          input.isSubmitting
        )
      }
    });
  }

  readonly correctWordInput: InputState;
  readonly misspelledWordsInput: InputState;

  setError(value: string | null) {
    this.error = value;
  }
}

